import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useStationHistory } from '@/hooks/useStationHistory';
import { useFilters } from '@/context/FiltersContext';
import { Skeleton } from '@/components/common/Skeleton';
import { FUEL_TYPE_COLORS, FUEL_TYPE_SHORT_LABELS } from '@/constants';
import type { FuelType } from '@/types';

interface PriceHistoryProps {
  stationId: string;
  className?: string;
}

export function PriceHistory({ stationId, className }: PriceHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, isLoading, error, refetch } = useStationHistory(stationId);
  const { selectedFuelTypes } = useFilters();

  const chartData = useMemo(() => {
    if (!data?.history) return [];

    return data.history
      .slice()
      .reverse()
      .map((entry) => ({
        timestamp: entry.timestamp,
        date: format(parseISO(entry.timestamp), 'dd/MM'),
        fullDate: format(parseISO(entry.timestamp), 'dd MMM yyyy'),
        ...entry.prices,
      }));
  }, [data]);

  const hasData = chartData.length > 0;

  return (
    <div className={clsx('border-t border-gray-100 pt-4', className)}>
      {/* Header with expand toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-1 group"
      >
        <h3 className="text-sm font-medium text-gray-700">Price History</h3>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3">
              {isLoading ? (
                <Skeleton className="h-48 w-full rounded-lg" />
              ) : error ? (
                <div className="text-center py-6">
                  <p className="text-sm text-red-600 mb-2">Failed to load history</p>
                  <button
                    type="button"
                    onClick={() => void refetch()}
                    className="text-sm text-brand-600 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              ) : !hasData ? (
                <div className="text-center py-8 text-gray-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-2 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <p className="text-sm">No price history available</p>
                  <p className="text-xs text-gray-400 mt-1">
                    History will appear after a few days
                  </p>
                </div>
              ) : (
                <>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 mb-3">
                    {selectedFuelTypes.map((fuelType) => (
                      <div key={fuelType} className="flex items-center gap-1.5">
                        <div
                          className="w-3 h-0.5 rounded-full"
                          style={{ backgroundColor: FUEL_TYPE_COLORS[fuelType] }}
                        />
                        <span className="text-xs text-gray-600">
                          {FUEL_TYPE_SHORT_LABELS[fuelType]}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Chart */}
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          domain={['dataMin - 2', 'dataMax + 2']}
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value: number) => `${value}p`}
                          width={40}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const entry = chartData.find((d) => d.date === label);
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-sm">
                                <p className="font-medium text-gray-900 mb-1">
                                  {entry?.fullDate || label}
                                </p>
                                {payload.map((p) => (
                                  <div
                                    key={p.dataKey}
                                    className="flex items-center justify-between gap-4"
                                  >
                                    <span
                                      className="flex items-center gap-1"
                                      style={{ color: p.color }}
                                    >
                                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                                      {FUEL_TYPE_SHORT_LABELS[p.dataKey as FuelType]}
                                    </span>
                                    <span className="font-medium">
                                      {typeof p.value === 'number' ? `${p.value.toFixed(1)}p` : 'N/A'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        {selectedFuelTypes.map((fuelType) => (
                          <Line
                            key={fuelType}
                            type="monotone"
                            dataKey={fuelType}
                            stroke={FUEL_TYPE_COLORS[fuelType]}
                            strokeWidth={2}
                            dot={false}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Last 7 days
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
